import { CHANNELS, Channel, NO_OWNER, NO_VOICE, SoundActor, channelOf, hookTrim, isPositional, playerPositional, positional } from "odyssey-shared";
import { type AudioSettings, type LevelId, type VolumeLevel } from "./audio-settings.ts";
import type { SoundBank } from "./bank.ts";
import { ACROSS_VISION, BY_DISTANCE, UNPLACED, applyDucking, applyLevels, channelNodes, followListener, playingVoices, priorityOf, setNow, silenceAllBut, stopAll, stopQuietly, type ChannelNodes } from "./channel-nodes.ts";
import type { ContextLike, GainLike, PannerLike } from "./graph.ts";
import type { AudioReport, AudioVoice } from "./report.ts";

/**
 * The mixer (ADR-085 decisions 8 to 10): Master and a gain per channel, each channel's ducking gain under it, and per
 * channel a voice table with each voice's gain and panner made once with the context. The context is made on the first
 * press (`unlock`), since browsers start no audio before one; a sound asked for before that, or before its buffer has
 * decoded, is recorded and dropped. A positional sound starts only inside the vision rectangle, and another player's
 * only within `PLAYER_SOUND_REACH_TILES`. It is placed: it stays where its source was when it started, and while its
 * voice plays its level and pan follow the listener (the amendment for P1.12-T10), set as it starts and eased to what
 * the listener's place gives each frame the page names that place (`listenerAt`). Its place is its offset from the
 * listener of the first frame after it starts, never from an earlier frame's, which a respawn, a snap or a world swap
 * may have left far away. A sound may name its owner, the thing it is of: an owned sound that starts fades out and
 * stops the sound its owner started before it (the amendment for P1.12-T9), and the level each placed voice is
 * followed to is written into the voice table, so a sound gone silent gives way first. A hidden window suspends the
 * context unless the player keeps sound in the background; one sound may be played through that suspension, alone
 * (`playThrough`, the amendment for P1.12-T7), which is how a whisper's alert is heard there.
 */

/**
 * How long an owner's earlier sound takes to stop when its next one starts, in seconds: its gain eases to silence with
 * a third of this as its time constant, about 5 per cent left at the end, and its source is stopped then. Shorter can
 * click; longer is heard under the new sound.
 */
const OWNER_FADE = 0.06;

/**
 * How many sounds of one hook play at once.
 */
const PER_HOOK = 3;

/**
 * The page's mixer.
 */
export class SoundMixer {
  private context: ContextLike | null = null;
  private master: GainLike | null = null;
  private readonly channels = new Map<Channel, ChannelNodes>();
  private readonly voiced: ChannelNodes[] = [];
  private readonly hookNumbers = new Map<string, number>();
  private readonly level = { gain: 1, pan: 0 };
  private readonly playing = (channel: Channel): number => this.channels.get(channel)?.table.playing ?? 0;
  private readonly unlockListeners: (() => void)[] = [];
  private readonly quietListeners: ((silent: boolean) => void)[] = [];
  private hidden = false;
  private alone = false;
  private through = 0;

  /**
   * @param makeContext Makes the audio context, or null where the browser has none.
   * @param bank The sounds.
   * @param settings The levels and the background choice.
   * @param report Where each sound asked for is recorded.
   */
  public constructor(
    private readonly makeContext: () => ContextLike | null,
    private readonly bank: SoundBank,
    private settings: AudioSettings,
    private readonly report: AudioReport,
  ) {
    report.held = () => this.alone;
  }

  /**
   * The context, once a press has made it.
   *
   * @returns The context, or null.
   */
  public get audio(): ContextLike | null {
    return this.context;
  }

  /**
   * Makes the context and its nodes on the first press, resumes it on any later one, and starts decoding the sounds.
   */
  public unlock(): void {
    if (this.context !== null) {
      if (this.context.state === "suspended" && !this.silenced()) {
        void this.context.resume().catch(() => undefined);
      }
      return;
    }
    const context = this.makeContext();
    if (context === null) {
      return;
    }
    this.context = context;
    const master = context.createGain();
    master.connect(context.destination);
    this.master = master;
    for (const spec of CHANNELS) {
      const nodes = channelNodes(context, spec, master, PER_HOOK);
      this.channels.set(spec.id, nodes);
      this.voiced.push(nodes);
    }
    applyLevels(this.context, this.master, this.channels, this.settings);
    this.report.unlocked = true;
    this.report.voices = () => this.voices();
    this.report.state = () => context.state;
    void this.bank.load(context);
    if (context.state === "suspended" && !this.silenced()) {
      void context.resume().catch(() => undefined);
    }
    for (const listener of this.unlockListeners) {
      listener();
    }
  }

  /**
   * Calls a listener once the context exists, at once when it already does.
   *
   * @param listener The listener.
   */
  public onUnlock(listener: () => void): void {
    this.unlockListeners.push(listener);
    if (this.context !== null) {
      listener();
    }
  }

  /**
   * Calls a listener whenever the music should pause or play on: the window's background mute or a hold to one sound (`holdAlone`) begins or ends.
   *
   * @param listener Told true when sound stops, false when it resumes.
   */
  public onQuiet(listener: (silent: boolean) => void): void {
    this.quietListeners.push(listener);
  }

  /**
   * A channel's input, for the music's own sources.
   *
   * @param channel The channel.
   * @returns Its volume gain, or null before the context exists.
   */
  public input(channel: Channel): GainLike | null {
    return this.channels.get(channel)?.volume ?? null;
  }

  /**
   * The levels in force.
   *
   * @returns The settings.
   */
  public get current(): AudioSettings {
    return this.settings;
  }

  /**
   * Sets Master's or a channel's level, heard at once.
   *
   * @param id Master or a channel.
   * @param level The level.
   */
  public setLevel(id: LevelId, level: VolumeLevel): void {
    this.settings = { ...this.settings, levels: { ...this.settings.levels, [id]: level } };
    applyLevels(this.context, this.master, this.channels, this.settings);
  }

  /**
   * Sets whether sound stops while the window is in the background.
   *
   * @param mute True to stop it.
   */
  public setBackgroundMute(mute: boolean): void {
    this.settings = { ...this.settings, backgroundMute: mute };
    this.applySilence();
  }

  /**
   * Sets whether a whisper's alert sounds while the window is in the background and muted. The mixer only holds the
   * choice with its other settings; the page's alert reads it.
   *
   * @param through True to let it sound.
   */
  public setWhisperBackground(through: boolean): void {
    this.settings = { ...this.settings, whisperBackground: through };
  }

  /**
   * Whether the window's background mute holds sound now.
   *
   * @returns True while the window is hidden with background mute on.
   */
  public get silent(): boolean {
    return this.silenced();
  }

  /**
   * Plays a hook's sound through the background mute, alone (ADR-085's amendment of 2026-10-13). While the mute does
   * not hold, it plays as `play` does. While it holds, every other channel's volume is set to silence and the
   * hook's own channel's other sounds are stopped, the suspended context is resumed, the sound starts, and as it
   * ends the context is suspended again if the mute still holds; the music is told of none of it and stays paused,
   * and while the mute holds no other sound starts, on any channel, though the context runs meanwhile.
   * The volumes come back as the window comes to the front. Nothing is resumed for a hook with no channel or no
   * sound, or before the first press has made the context; where the browser will not resume a context in the
   * background the sound is dropped.
   *
   * @param hook The hook.
   * @param actor Who the sound belongs to; it is played with no place.
   * @returns True when the sound started or, through the mute, was asked of a context being resumed.
   */
  public playThrough(hook: string, actor: SoundActor): boolean {
    if (!this.silenced() || this.alone) {
      return this.play(hook, actor);
    }
    const context = this.context;
    const channel = channelOf(hook, actor);
    if (context === null || channel === null || this.bank.resolve(hook) === null) {
      this.report.record(hook, channel ?? "", false);
      return false;
    }
    silenceAllBut(this.voiced, channel, context.currentTime, this.through === 0);
    this.through += 1;
    void context
      .resume()
      .catch(() => undefined)
      .then(() => {
        const started = this.start(hook, channel, actor, 0, 0, NO_OWNER, () => this.throughEnded(), true);
        this.report.record(hook, channel, started);
        if (!started) {
          this.throughEnded();
        }
      });
    return true;
  }

  /**
   * Holds the page's sound to what `playAlone` plays (maintenance): the music is told to pause, every sound that
   * plays is stopped, and nothing else starts, on any channel, a whisper's alert through the background mute
   * included, until `endAlone`. Levels and settings are not touched.
   */
  public holdAlone(): void {
    this.alone = true;
    stopAll(this.voiced);
    this.tellQuiet();
  }

  /**
   * Holds the page's sound as `holdAlone` does and plays one hook through the hold, as the only sound. A hook already
   * playing alone may be played again.
   *
   * @param hook The hook.
   * @param actor Who the sound belongs to; it is played with no place.
   * @returns True when the sound started; the hold stands either way.
   */
  public playAlone(hook: string, actor: SoundActor): boolean {
    this.holdAlone();
    const channel = channelOf(hook, actor);
    if (channel === null) {
      this.report.record(hook, "", false);
      return false;
    }
    const started = this.start(hook, channel, actor, 0, 0, NO_OWNER, undefined, true);
    this.report.record(hook, channel, started);
    return started;
  }

  /**
   * Ends the hold of `holdAlone`: sounds start again and the music, told to resume unless the window's background
   * mute holds it, plays on from where it paused. Nothing happens when no hold stands.
   */
  public endAlone(): void {
    if (this.alone) {
      this.alone = false;
      this.tellQuiet();
    }
  }

  /**
   * Counts a sound played through the background mute as ended, and suspends the context again once the last has,
   * if the mute still holds.
   */
  private throughEnded(): void {
    this.through -= 1;
    if (this.through === 0 && this.silenced()) {
      void this.context?.suspend().catch(() => undefined);
    }
  }

  /**
   * Tells the mixer whether the window is in the background.
   *
   * @param hidden Whether the document is hidden.
   */
  public setHidden(hidden: boolean): void {
    this.hidden = hidden;
    this.applySilence();
  }

  /**
   * Names where the listener is, the page's own character as it is drawn, each frame it is drawn, and eases every
   * placed voice that plays to the level and pan that place gives it (`placedLevel`): quieter as the listener walks
   * away, silent out of reach with the sound still running, back as the listener returns, panned to the side its
   * source is on. A voice whose level and pan come out as last asked is left alone, so a listener standing still
   * asks nothing of the audio; nothing here allocates. A voice started since the last frame takes this listener as
   * its anchor and is asked nothing: its offset was measured from where the listener is now, so its level is right
   * as it stands.
   *
   * @param x Where the listener is across, in tiles.
   * @param y The same down.
   */
  public listenerAt(x: number, y: number): void {
    const context = this.context;
    if (context === null) {
      return;
    }
    followListener(this.voiced, x, y, context.currentTime, this.level);
  }

  /**
   * The voices playing now, for the report: each with its hook, its channel, whether it is placed and the level and
   * pan last asked of it.
   *
   * @returns The voices in channel then voice order, built when read.
   */
  private voices(): AudioVoice[] {
    return playingVoices(this.voiced);
  }

  /**
   * Plays a hook's sound. A positional sound, of any actor but the page's own character, is placed by its offset
   * from the listener and dropped outside the vision rectangle; another player's fades by its distance
   * (`playerPositional`) and is dropped, starting no node, at or beyond `PLAYER_SOUND_REACH_TILES`. A hook a trim
   * names (`HOOK_TRIMS`, a footstep) plays at the trim's level under that. A placed sound stays at that offset from
   * where the listener is in the next frame named (`listenerAt`), and its level and pan follow the listener from
   * then on. A sound that names an owner and starts fades out and stops the sound that owner started before it on
   * the same channel, if it still plays (`OWNER_FADE`); one that starts nothing stops nothing.
   *
   * @param hook The hook.
   * @param actor Who the sound belongs to.
   * @param dx The source's offset across from the listener, in tiles, for a positional sound.
   * @param dy The offset down, in tiles.
   * @param owner Whose sound it is, a whole number of 0 or more (a thing's handle); `NO_OWNER` by default.
   * @returns True when a buffer started.
   */
  public play(hook: string, actor: SoundActor, dx = 0, dy = 0, owner: number = NO_OWNER): boolean {
    const channel = channelOf(hook, actor);
    if (channel === null) {
      this.report.record(hook, "", false);
      return false;
    }
    const started = this.start(hook, channel, actor, dx, dy, owner);
    this.report.record(hook, channel, started);
    return started;
  }

  /**
   * Starts a hook's sound on its channel's voice, or nothing. A placed sound keeps its offset and has no anchor
   * until the next frame names the listener: whoever measured the offset measured it from where the listener is now,
   * which the last frame drawn may not show.
   *
   * @param hook The hook.
   * @param channel Its channel.
   * @param actor Who it belongs to.
   * @param dx The offset across, in tiles.
   * @param dy The offset down, in tiles.
   * @param owner Whose sound it is, or `NO_OWNER`.
   * @param ended Told once when the sound's source ends, whether or not its voice was taken meanwhile; not told
   *   when nothing started.
   * @param throughMute True for the one sound `playThrough` plays; while the mute holds, no other starts.
   * @returns True when a buffer started.
   */
  private start(hook: string, channel: Channel, actor: SoundActor, dx: number, dy: number, owner: number, ended?: () => void, throughMute = false): boolean {
    const context = this.context;
    const nodes = this.channels.get(channel);
    const sounding = this.bank.resolve(hook);
    if (context === null || nodes === undefined || sounding === null || context.state !== "running" || (!throughMute && (this.silenced() || this.alone))) {
      return false;
    }
    const placed = isPositional(actor);
    const byDistance = actor === SoundActor.Player;
    const trim = hookTrim(hook);
    this.level.gain = trim;
    this.level.pan = 0;
    if (placed) {
      if (!(byDistance ? playerPositional(dx, dy, this.level) : positional(dx, dy, this.level))) {
        return false;
      }
      this.level.gain *= trim;
    }
    const buffer = this.bank.pick(sounding);
    if (buffer === null) {
      return false;
    }
    const number = this.hookNumber(sounding);
    const priority = priorityOf(channel, actor);
    const earlier = nodes.table.heldBy(owner);
    if (earlier !== NO_VOICE) {
      this.fadeOut(nodes, earlier, context.currentTime);
    }
    const voice = nodes.table.claim(number, priority, this.level.gain);
    if (voice === NO_VOICE) {
      return false;
    }
    stopQuietly(nodes.sources[voice] ?? null);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(nodes.gains[voice] as GainLike);
    setNow((nodes.gains[voice] as GainLike).gain, this.level.gain, context.currentTime);
    setNow((nodes.panners[voice] as PannerLike).pan, this.level.pan, context.currentTime);
    nodes.sources[voice] = source;
    nodes.hooks[voice] = hook;
    nodes.heard[voice] = placed ? (byDistance ? BY_DISTANCE : ACROSS_VISION) : UNPLACED;
    nodes.offsetX[voice] = dx;
    nodes.offsetY[voice] = dy;
    nodes.anchored[voice] = 0;
    nodes.trims[voice] = trim;
    nodes.levels[voice] = this.level.gain;
    nodes.pans[voice] = this.level.pan;
    nodes.table.start(voice, number, priority, this.level.gain, context.currentTime, owner);
    source.onended = () => {
      if (nodes.sources[voice] === source) {
        nodes.sources[voice] = null;
        nodes.heard[voice] = UNPLACED;
        nodes.table.release(voice);
        applyDucking(this.context, this.channels, this.playing);
      }
      ended?.();
    };
    source.start();
    applyDucking(this.context, this.channels, this.playing);
    return true;
  }

  /**
   * Fades a playing voice to silence and stops its source `OWNER_FADE` on. Until it ends the voice has no owner, is
   * placed no longer, so a walking listener's frames ask nothing more of it, and counts as silent in the voice table,
   * so it gives way first if voices run out meanwhile; its source's end frees it as any sound's does.
   *
   * @param nodes The channel.
   * @param voice The voice's index.
   * @param now The context's time.
   */
  private fadeOut(nodes: ChannelNodes, voice: number, now: number): void {
    nodes.table.disown(voice);
    nodes.heard[voice] = UNPLACED;
    nodes.levels[voice] = 0;
    (nodes.gains[voice] as GainLike).gain.setTargetAtTime(0, now, OWNER_FADE / 3);
    nodes.sources[voice]?.stop(now + OWNER_FADE);
  }

  /**
   * A hook's number in the voice tables, given on first use.
   *
   * @param hook The hook.
   * @returns Its number.
   */
  private hookNumber(hook: string): number {
    let number = this.hookNumbers.get(hook);
    if (number === undefined) {
      number = this.hookNumbers.size;
      this.hookNumbers.set(hook, number);
    }
    return number;
  }

  /**
   * Whether the window's background mute holds sound now.
   *
   * @returns True while hidden with background mute on.
   */
  private silenced(): boolean {
    return this.hidden && this.settings.backgroundMute;
  }

  /**
   * Suspends or resumes the context for the background mute and tells the listeners. A sound being played through
   * the mute is left to finish: its end suspends the context. Coming out of the mute sets every volume from the
   * settings again, which a sound played through it had silenced.
   */
  private applySilence(): void {
    const silent = this.silenced();
    const context = this.context;
    if (context !== null && !(silent && this.through > 0)) {
      void (silent ? context.suspend() : context.resume()).catch(() => undefined);
    }
    if (!silent) {
      applyLevels(this.context, this.master, this.channels, this.settings);
    }
    this.tellQuiet();
  }

  /**
   * Tells the listeners whether the music should be paused: while the window's mute or a `playAlone` hold stands.
   */
  private tellQuiet(): void {
    const silent = this.silenced() || this.alone;
    for (const listener of this.quietListeners) {
      listener(silent);
    }
  }
}
